/**
 * Parse a Coveo `highlightedValue` into the pieces the dropdown renders.
 *
 * The SDK escapes the suggestion before wrapping the part the shopper has not
 * typed in the engine's `<b>` delimiters (`getHighlightedSuggestion` →
 * `escapeHtml`), so the string reaching us carries entities, not markup:
 * `cats & dogs` arrives as `cats &amp; dogs`. Rendering it as React text is
 * safe but shows the entity spelling, so the text segments are decoded back.
 * Only the six characters the SDK escapes are recognised, in a single pass, so
 * a suggestion for the literal text `&amp;` survives the round trip.
 */
const ESCAPED_CHARACTERS: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#x27;': "'",
  '&#x60;': '`',
};

const ESCAPED_PATTERN = /&(?:amp|lt|gt|quot|#x27|#x60);/g;

const decode = (text: string) =>
  text.replace(ESCAPED_PATTERN, (escaped) => ESCAPED_CHARACTERS[escaped]);

export type HighlightSegment = {
  text: string;
  /** The part Coveo marked as not yet typed, which the dropdown bolds. */
  emphasized: boolean;
};

export function highlightSegments(
  highlightedValue: string
): HighlightSegment[] {
  return highlightedValue
    .split(/(<b>.*?<\/b>)/g)
    .filter((part) => part.length > 0)
    .map((part) => {
      const emphasized = part.match(/^<b>(.*?)<\/b>$/);
      return emphasized
        ? { text: decode(emphasized[1]), emphasized: true }
        : { text: decode(part), emphasized: false };
    });
}
